import { useState } from 'react';
import { Mail, MapPin, Pencil } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SectionHeader } from '@/components/ui/section-header';
import { SoftCard } from '@/components/ui/soft-card';

const EMPTY_ADDRESS = { line1: '', line2: '', city: '', postal_code: '', country: 'GB' };

/**
 * Billing address card with inline view/edit. Save is currently a design-mock
 * — wire-up to `POST /api/billing/address/{shopId}` is NEA-5770 territory.
 * Email is stored alongside the address on the Stripe customer.
 */
export function BillingAddressCard({ address, email, onSave }) {
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState({ ...(address ?? EMPTY_ADDRESS), email: email ?? '' });

	const startEdit = () => {
		setDraft({ ...(address ?? EMPTY_ADDRESS), email: email ?? '' });
		setEditing(true);
	};

	const save = () => {
		onSave?.(draft);
		setEditing(false);
	};

	const cancel = () => setEditing(false);

	const setField = (key) => (event) => setDraft((prev) => ({ ...prev, [key]: event.target.value }));

	const hasAddress = address && (address.line1 || address.city || address.postal_code);

	return (
		<section>
			<SectionHeader
				eyebrow="Billing details"
				title="Billing address"
				sub="Used on your Unify invoices and for tax."
				rightSlot={
					!editing ? (
						<Button variant="outline" size="sm" onClick={startEdit}>
							<Pencil className="h-4 w-4" />
							{hasAddress ? 'Edit' : 'Add address'}
						</Button>
					) : null
				}
			/>
			<SoftCard className="!p-5 border-border">
				{!editing ? (
					<div className="grid gap-5 sm:grid-cols-2">
						<AddressView address={address} hasAddress={hasAddress} />
						<EmailView email={email} />
					</div>
				) : (
					<form
						className="space-y-4"
						onSubmit={(event) => {
							event.preventDefault();
							save();
						}}
					>
						<div className="grid gap-4 sm:grid-cols-2">
							<Field
								id="line1"
								label="Address line 1"
								value={draft.line1}
								onChange={setField('line1')}
								placeholder="Street and number"
							/>
							<Field
								id="line2"
								label="Address line 2"
								value={draft.line2}
								onChange={setField('line2')}
								placeholder="Unit, floor, etc."
							/>
							<Field id="city" label="City" value={draft.city} onChange={setField('city')} />
							<Field id="postal_code" label="Postal code" value={draft.postal_code} onChange={setField('postal_code')} />
							<Field
								id="country"
								label="Country"
								value={draft.country}
								onChange={setField('country')}
								placeholder="2-letter code, e.g. GB"
							/>
							<Field id="email" label="Billing email" type="email" value={draft.email} onChange={setField('email')} />
						</div>
						<div className="flex flex-wrap justify-end gap-2">
							<Button type="button" variant="ghost" onClick={cancel}>
								Cancel
							</Button>
							<Button type="submit">Save changes</Button>
						</div>
					</form>
				)}
			</SoftCard>
		</section>
	);
}

function AddressView({ address, hasAddress }) {
	return (
		<div className="flex items-start gap-3">
			<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground">
				<MapPin className="h-4 w-4" />
			</div>
			<div className="min-w-0 space-y-1">
				<div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Address</div>
				{hasAddress ? (
					<div className="text-sm text-foreground" data-mf-replace="**REMOVED**">
						<div>{address.line1}</div>
						{address.line2 ? <div>{address.line2}</div> : null}
						<div>
							{[address.city, address.postal_code].filter(Boolean).join(', ')}
							{address.country ? `, ${address.country}` : ''}
						</div>
					</div>
				) : (
					<div className="text-sm text-muted-foreground">No address on file yet.</div>
				)}
			</div>
		</div>
	);
}

function EmailView({ email }) {
	return (
		<div className="flex items-start gap-3">
			<div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent text-accent-foreground">
				<Mail className="h-4 w-4" />
			</div>
			<div className="min-w-0 space-y-1">
				<div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">Billing email</div>
				<div className="text-sm text-foreground" data-mf-replace="**REMOVED**">
					{email || <span className="text-muted-foreground">No billing email yet.</span>}
				</div>
			</div>
		</div>
	);
}

function Field({ id, label, type = 'text', value, onChange, placeholder }) {
	return (
		<div className="space-y-1.5">
			<Label htmlFor={id}>{label}</Label>
			<Input id={id} type={type} value={value ?? ''} onChange={onChange} placeholder={placeholder} />
		</div>
	);
}
